/**
 * JournalChurnPanel (Iter 36.e) — "Top write churn" widget for the
 * modernization dashboard.
 *
 * Renders only when DSPJRN entries have actually been ingested (we hide
 * silently if totals.total_entries === 0 — empty workspaces shouldn't be
 * shouting "no data" cards at the consultant).
 *
 * Three sub-sections, all driven by /workspaces/{ws}/metadata/journal-rollup:
 *   1. Header strip — total writes, distinct objects + programs, window,
 *      and receiver-chain health (attached / detached / retained bytes).
 *   2. Two tables side-by-side: top files by write count vs top programs by
 *      write count, with insert/update/delete splits.
 *   3. Tiny PT/UP/DL distribution chips so the journal-code mix is glanceable.
 *
 * Why this is its own page-mounted component (not a row in
 * WorkspaceMetadataDashboard.jsx): the rollup is a separate round-trip from
 * the dashboard rollup; isolating the fetch lets the dashboard paint
 * immediately and the churn panel populate when the journal endpoint
 * resolves, without delaying the rest of the page.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClockClockwiseIcon as ClockClockwise,
  TargetIcon as Target,
  CodeIcon as Code,
  WaveformIcon as Waveform,
  ArchiveIcon as Archive,
  CircleNotchIcon as Spinner,
  ArrowRightIcon as ArrowRight,
} from "@phosphor-icons/react";
import { getJournalRollup } from "@/lib/api";

function fmtBytes(b) {
  if (b == null || b === 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function fmtIsoDate(iso) {
  // The server (Iter 36.e) normalises every timestamp to ISO-8601 strings.
  // Take only the YYYY-MM-DD prefix — datetime precision is irrelevant here.
  if (!iso) return "—";
  return String(iso).slice(0, 10);
}

const KIND_COLOR = {
  PT: { bg: "#defbe6", fg: "#0e6027" },   // green — insert
  PX: { bg: "#defbe6", fg: "#0e6027" },
  UP: { bg: "#edf5ff", fg: "#0043ce" },   // blue — update
  UB: { bg: "#edf5ff", fg: "#0043ce" },
  DL: { bg: "#fff1f1", fg: "#a2191f" },   // red — delete
  CT: { bg: "#f4f4f4", fg: "#525252" },   // grey — commit/admin
  RB: { bg: "#fcf4d6", fg: "#a56600" },   // amber — rollback
};

function KindChip({ kind, count }) {
  const c = KIND_COLOR[kind] || { bg: "#f4f4f4", fg: "#525252" };
  return (
    <span
      className="ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 inline-flex items-center gap-1"
      style={{ background: c.bg, color: c.fg }}
      data-testid={`churn-kind-${kind}`}
    >
      {kind}
      <span className="font-semibold tabular-nums">{count.toLocaleString()}</span>
    </span>
  );
}

function StatCell({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
        {label}
      </div>
      <div className="text-xl font-semibold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

export default function JournalChurnPanel({ workspaceId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getJournalRollup(workspaceId, { topN: 10 })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workspaceId]);

  if (loading) {
    return (
      <div className="ml-card p-4" data-testid="churn-loading">
        <div className="flex items-center gap-2 text-sm text-[var(--ml-text-secondary)]">
          <Spinner size={14} className="animate-spin" />
          Loading journal churn…
        </div>
      </div>
    );
  }
  // No data + no entries = silently hide. Same shape as the rest of the
  // dashboard sections that auto-hide when irrelevant.
  if (!data || data.totals?.total_entries === 0) return null;

  const t = data.totals;
  const windowLabel = `${fmtIsoDate(t.window_start)} → ${fmtIsoDate(t.window_end)}`;

  return (
    <div className="space-y-4" data-testid="journal-churn-panel">
      <div className="ml-card p-4" data-testid="churn-header">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
              From journal entries (DSPJRN) + receivers (DSPJRNRCV)
            </div>
            <div className="text-base font-semibold mt-1 flex items-center gap-1.5">
              <Waveform size={16} weight="duotone" color="#0f62fe" />
              Top write churn &middot; what the journal proves is actually changing
            </div>
          </div>
          <Link
            to={`/workspaces/${workspaceId}/metadata`}
            data-testid="churn-import-link"
            className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1"
          >
            Manage import
            <ArrowRight size={11} weight="bold" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCell
            label="Total writes"
            value={t.write_entries.toLocaleString()}
            testid="churn-stat-writes"
          />
          <StatCell
            label="Distinct files"
            value={t.distinct_objects.toLocaleString()}
            testid="churn-stat-files"
          />
          <StatCell
            label="Distinct programs"
            value={t.distinct_programs.toLocaleString()}
            testid="churn-stat-programs"
          />
          <StatCell
            label="Receivers"
            value={`${t.attached_count.toLocaleString()} att · ${t.detached_count.toLocaleString()} det`}
            testid="churn-stat-receivers"
          />
          <StatCell
            label="Retained on disk"
            value={fmtBytes(t.total_receiver_bytes)}
            testid="churn-stat-bytes"
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-[var(--ml-text-secondary)]">
          <ClockClockwise size={12} weight="bold" />
          <span data-testid="churn-window">Window: {windowLabel}</span>
          {t.oldest_attached_at && (
            <>
              <span aria-hidden="true">·</span>
              <Archive size={12} weight="bold" />
              <span data-testid="churn-oldest-attached">
                Oldest attached: {fmtIsoDate(t.oldest_attached_at)}
              </span>
            </>
          )}
        </div>

        {data.by_entry_type.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="churn-kind-strip">
            {data.by_entry_type.map((row) => (
              <KindChip key={row.entry_type} kind={row.entry_type} count={row.count} />
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="ml-card p-4" data-testid="churn-top-objects">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                By write volume
              </div>
              <div className="text-base font-semibold mt-1 flex items-center gap-1.5">
                <Target size={16} weight="duotone" color="#0f62fe" />
                Top files actually written
              </div>
            </div>
          </div>
          {data.top_objects.length === 0 ? (
            <div className="py-6 text-center text-sm text-[var(--ml-text-secondary)] border border-dashed border-[var(--ml-border)]">
              Journal entries imported but no PT/UP/DL writes against any object.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="churn-top-objects-table">
              <thead>
                <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
                  <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">File</th>
                  <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Writes</th>
                  <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Ins</th>
                  <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Upd</th>
                  <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Del</th>
                </tr>
              </thead>
              <tbody>
                {data.top_objects.map((row) => (
                  <tr
                    key={`${row.library_name || ""}/${row.file_name}`}
                    className="border-b border-[var(--ml-border)] last:border-b-0"
                    data-testid={`churn-obj-row-${row.file_name}`}
                  >
                    <td className="px-2 py-1.5 font-mono text-xs">
                      {row.library_name && (
                        <span className="text-[var(--ml-text-secondary)]">{row.library_name}/</span>
                      )}
                      {row.file_name}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{row.total_writes.toLocaleString()}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-[#0e6027]">{row.inserts || "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-[#0043ce]">{row.updates || "—"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-[#a2191f]">{row.deletes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="ml-card p-4" data-testid="churn-top-programs">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                By write volume
              </div>
              <div className="text-base font-semibold mt-1 flex items-center gap-1.5">
                <Code size={16} weight="duotone" color="#0f62fe" />
                Top write-issuing programs
              </div>
            </div>
          </div>
          {data.top_programs.length === 0 ? (
            <div className="py-6 text-center text-sm text-[var(--ml-text-secondary)] border border-dashed border-[var(--ml-border)]">
              No program names found on journal entries (JOPGM was blank in the import).
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="churn-top-programs-table">
              <thead>
                <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
                  <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Program</th>
                  <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Writes</th>
                  <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Files</th>
                  <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Ins/Upd/Del</th>
                </tr>
              </thead>
              <tbody>
                {data.top_programs.map((row) => (
                  <tr
                    key={row.program_name}
                    className="border-b border-[var(--ml-border)] last:border-b-0"
                    data-testid={`churn-pgm-row-${row.program_name}`}
                  >
                    <td className="px-2 py-1.5 font-mono text-xs">{row.program_name}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{row.total_writes.toLocaleString()}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{row.distinct_objects}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-[var(--ml-text-secondary)]">
                      <span className="text-[#0e6027]">{row.inserts || 0}</span>
                      <span className="opacity-50 mx-0.5">/</span>
                      <span className="text-[#0043ce]">{row.updates || 0}</span>
                      <span className="opacity-50 mx-0.5">/</span>
                      <span className="text-[#a2191f]">{row.deletes || 0}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
